/**
 * Conversation and message API for chat.
 * Aligned with ann-web-app `src/services/conversations.js`; uses injected `apiClient` from the host
 * (same pattern as `apps/workspaces/src/services/*.js`, but first argument is the client instance).
 *
 * @param {import('axios').AxiosInstance} apiClient
 */

import { createTenantFile } from './files.js';

/** Default axios timeout for `sendMessage` — the API can hold the request well beyond the api-client default (10s). */
export const SEND_ASYNCHRONOUS_AXIOS_TIMEOUT_MS = 180_000;

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {{ userId: string, agentId: string }} params
 */
export const listConversations = async (apiClient, { userId, agentId }) => {
  const payload = {
    userUUid: userId,
    agentUuid: agentId,
  };

  return apiClient.post('/v1/conversation/list', payload);
};

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {{ conversationId: string }} params
 */
export const getConversationMessages = async (apiClient, { conversationId }) => {
  const payload = { conversationUuid: conversationId };

  return apiClient.post('/v1/message/list', payload);
};

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {File} file
 * @param {{ agentUuid?: string, conversationUuid?: string }} [options]
 * @returns {Promise<{ fileUuid: string, name: string, fileType: string }>}
 */
export const uploadChatAttachment = async (apiClient, file, options = {}) => {
  if (!file || !(file instanceof File)) {
    throw new Error('uploadChatAttachment requires a File');
  }
  const { agentUuid, conversationUuid } = options;
  const name = file.name || 'attachment';
  const fileType =
    (name.includes('.') ? name.split('.').pop() : '') || file.type || 'text/plain';
  const createFileBody = {
    name,
    folder_id: '',
    file_type: file.type || 'text/plain',
    status: 'active',
    agent_uuid: agentUuid || '',
    conversation_uuid: conversationUuid || '',
  };

  const data = await createTenantFile(apiClient, createFileBody);
  const body = data?.body ?? data;
  const uploadUrl = body?.uploadUrl ?? body?.upload_url;
  if (!uploadUrl) {
    throw new Error('Upload URL not returned from createFile');
  }

  const putResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!putResponse.ok) {
    throw new Error(`Upload failed: ${putResponse.status}`);
  }

  const fileUuid =
    body?.file?.uuid ??
    body?.uuid ??
    body?.id ??
    body?.file_id ??
    body?.file_uuid;
  if (!fileUuid) {
    console.warn(
      'createFile did not return file uuid; send-asynchronous file_uuids may not work.',
    );
  }
  return { fileUuid, name, fileType };
};

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {object} params
 * @param {string} params.messageText
 * @param {string} params.conversationUuid
 * @param {string} params.agentUuid
 * @param {string} [params.channel]
 * @param {string[]} [params.fileUuids]
 * @param {object} [params.llmConfig]
 * @param {object} [params.context]
 * @param {import('axios').AxiosRequestConfig} [axiosConfig] — e.g. `{ timeout: 180000 }` for long-running sends (AI / send-asynchronous).
 */
export const sendMessage = async (
  apiClient,
  {
    messageText,
    conversationUuid,
    agentUuid,
    channel = 'WEB',
    fileUuids,
    llmConfig,
    context,
  },
  axiosConfig = {},
) => {
  const payload = {
    message_text: messageText,
    assistant_uuid: agentUuid ?? '',
    conversation_uuid: conversationUuid ?? '',
    channel: channel ?? 'WEB',
  };

  if (fileUuids && Array.isArray(fileUuids) && fileUuids.length > 0) {
    payload.file_uuids = fileUuids;
  }

  if (llmConfig && typeof llmConfig === 'object') {
    payload.llm_config = llmConfig;
  }

  if (context && typeof context === 'object' && Object.keys(context).length > 0) {
    payload.context = context;
  }

  return apiClient.post('/v1/message/send-asynchronous', payload, axiosConfig);
};

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {string} conversationId
 */
export const deleteConversation = async (apiClient, conversationId) => {
  return apiClient.delete(`/conversation/${conversationId}`);
};

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {{ assistantUuid: string, title?: string }} params
 */
export const createConversation = async (apiClient, { assistantUuid, title }) => {
  const payload = {
    assistant_uuid: assistantUuid,
  };

  if (title) {
    payload.title = title;
  }

  try {
    return await apiClient.post('/v1/workspace/conversation/create', payload);
  } catch (error) {
    const responseData = error?.raw?.response?.data;
    if (responseData) {
      const apiError = new Error(
        responseData.message ||
          responseData.msg_code?.[0]?.description ||
          error.message,
      );
      apiError.status_code = responseData.status_code || error.raw.response.status;
      apiError.msg_code = responseData.msg_code || [];
      throw apiError;
    }

    throw error;
  }
};
